import {
  ReactElement,
  useEffect,
  useContext,
  useReducer,
  useCallback,
} from "react";

import dayjs from "dayjs";
import { Button, Heading, Section } from "react-bulma-components";
import styled from "styled-components";

import {
  createMovementLog,
  updateMovementLog,
  getMovementRouteByAPIResponse,
} from "api/movement_logs";
import {
  Navbar,
  PriceLabel,
  SystemNotification,
  TravelExpensesTable,
  TravelExpenseApplicationDialog,
} from "components";
import { MoveMonthButtonGroup } from "components/atoms/Button";
import { CommuterPassesList } from "components/CommuterPassList/CommuterPassList";
import { CommutingRouteProp } from "components/CommutingRouteDialog/CommutingRouteDialog";
import { CountDetailList } from "components/CountDetailList/CountDetailList";
import { CountLabel } from "components/CountLabel/CountLabel";
import { GlobalErrorScreen } from "components/GlobalErrorScreen";
import { APPLICATION_STATUS } from "enums/applicationStatus";
import { showCommentInHTML, getFormattedComment } from "helpers/comment";
import { isOverCutOffDate, getMonthString } from "helpers/date";
import { RESPONSIVE } from "helpers/responsive";
import {
  useGetMovementLogsApi,
  useGetMovementLogsMovementLogIdMovementRoutesApi,
  useDelMovementLogApi,
  useGetUserAddressApi,
  useCreateTransportationExpenseApi,
  useUpdateMovementLogApprovalStatusApi,
} from "hooks/api";
import Address from "models/address";
import { NewMovementLog } from "models/movementLog";
import { MovementRoute } from "models/movementRoute";
import { PreparedRoute } from "models/preparedRoute";
import { filterZeroFareBySystem } from "models/transportationExpense";
import {
  MonthSummary,
  DaySummary,
  CommutingExpense,
  getMonthSummaryByResult,
  getDaySummariesByResult,
} from "models/travelExpense";
import "./TravelExpenses.css";
import * as Layout from "molecules/Layout";
import * as Table from "molecules/Table";
import { AuthContext } from "providers/CognitoAuthProvider";

export interface TravelExpensesState {
  user: {
    employeeId?: number;
    name?: string;
  };
  monthSummary: MonthSummary;
  daySummaries: DaySummary[];
  isMovementLogsLoaded: boolean;
  expandedTravelExpenses: string[];
  isCommutingRouteDialogVisible: boolean;
  commutingRouteProp: CommutingRouteProp;
  hasOutwardPath: boolean;
  hasReturnPath: boolean;
  isReturnPath: boolean;
  isWorkplaceDialogVisible: boolean;
  isTravelExpenseApplicationDialogVisible: boolean;
  isRegisterCommutingRouteInProgress: boolean;
  isTransportationExpenseDialogVisible: boolean;
  transportationExpenseDaySummary: DaySummary;
  isUpdateTravelExpenseApplicationDialogCompleted: boolean;
  isUpdateTravelExpenseApplicationDialogInProgress: boolean;
  isHomeAddressNotLoaded: boolean;
}

const initialState: TravelExpensesState = {
  user: {
    employeeId: undefined,
    name: undefined,
  },
  monthSummary: {
    movementLogApprovalId: undefined,
    applicationStatus: "",
    applicationStatusName: "",
    workCount: 0,
    totalDistanceKm: 0,
    totalFare: 0,
    totalTime: 0,
    applicantComment: undefined,
    approverComment: undefined,
    commuterPasses: [],
    commutingExpenses: {
      publicTransportationCount: 0,
      publicTransportationList: [],
      bicycleCount: 0,
      bicycleList: [],
      myCarCount: 0,
      myCarList: [],
      totalFare: 0,
    },
    travelExpenses: {
      publicTransportationCount: 0,
      publicTransportationList: [],
      totalFare: 0,
    },
  },
  daySummaries: [],
  isMovementLogsLoaded: false,
  expandedTravelExpenses: [],

  isCommutingRouteDialogVisible: false,
  isWorkplaceDialogVisible: false,
  isTravelExpenseApplicationDialogVisible: false,
  isRegisterCommutingRouteInProgress: false,
  isTransportationExpenseDialogVisible: false,
  isUpdateTravelExpenseApplicationDialogCompleted: false,
  isUpdateTravelExpenseApplicationDialogInProgress: false,

  commutingRouteProp: {
    movementLogId: 0,
    at: "",
    arrivalId: 0,
    arrivalName: "",
    departureId: 0,
  } as CommutingRouteProp,
  hasOutwardPath: false,
  hasReturnPath: false,
  isReturnPath: false,
  transportationExpenseDaySummary: {} as DaySummary,
  isHomeAddressNotLoaded: false,
};

interface ToggleTransitsAction {
  type: "TOGGLE_TRANSITS";
  payload: {
    id: string;
  };
}

interface ToggleCommutingRouteDialogAction {
  type: "TOGGLE_COMMUTING_ROUTE_DIALOG";
  payload: {
    commutingExpense?: CommutingExpense;
    hasOutwardPath?: boolean;
    hasReturnPath?: boolean;
    isReturnPath?: boolean;
  };
}

interface ToggleIsRegisterCommutingRouteInProgress {
  type: "TOGGLE_IS_REGISTER_COMMUTING_ROUTE_IN_PROGRESS";
  payload: {
    isRegisterCommutingRouteInProgress: boolean;
  };
}

interface ToggleWorkplaceDialogAction {
  type: "TOGGLE_WORKPLACE_DIALOG";
}

interface ChangeWorkplaceAction {
  type: "CHANGE_WORKPLACE";
  payload: {
    commutingRouteProp: CommutingRouteProp;
    workplace: Address;
  };
}

interface ToggleTravelExpenseApplicationDialogAction {
  type: "TOGGLE_TRAVEL_EXPENSE_APPLICATION_DIALOG";
}

interface SetUserAction {
  type: "SET_USER";
  payload: {
    employeeId: number;
    name: string;
  };
}

interface SetUserAddressIdAction {
  type: "SET_USER_ADDRESS";
  payload: {
    address?: Address;
  };
}

interface SetMovementLogsAction {
  type: "SET_MOVEMENT_LOGS";
  payload: {
    monthSummary: MonthSummary;
    daySummaries: DaySummary[];
  };
}

interface SetIsMovementLogsLoadedAction {
  type: "SET_IS_MOVEMENT_LOGS_LOADED";
  payload: {
    isMovementLogsLoaded: boolean;
  };
}

interface SetMovementRoutesAction {
  type: "SET_MOVEMENT_ROUTES";
  payload: {
    results: {
      id: number;
      movementRoutes: MovementRoute[];
      isCommutingExpense: boolean;
      isTransportationExpense: boolean;
    }[];
  };
}

interface ToggleTransportationExpenseDialogAction {
  type: "TOGGLE_TRANSPORTATION_EXPENSE_DIALOG";
  payload: {
    daySummary: DaySummary;
  };
}

interface SetIsTravelExpenseApplicationDialogCompletedAction {
  type: "SET_IS_UPDATE_TRAVEL_EXPENSE_APPLICATION_DIALOG_COMPLETED";
  payload: {
    isUpdateTravelExpenseApplicationDialogCompleted: boolean;
  };
}

interface SetIsTravelExpenseApplicationDialogInProgressAction {
  type: "SET_IS_UPDATE_TRAVEL_EXPENSE_APPLICATION_DIALOG_IN_PROGRESS";
  payload: {
    isUpdateTravelExpenseApplicationDialogInProgress: boolean;
  };
}

interface SetMonthSummaryAction {
  type: "SET_MONTH_SUMMARY";
  payload: {
    monthSummary: MonthSummary;
  };
}

type TravelExpensesActions =
  | ToggleTransitsAction
  | ToggleCommutingRouteDialogAction
  | ToggleIsRegisterCommutingRouteInProgress
  | ToggleWorkplaceDialogAction
  | ChangeWorkplaceAction
  | ToggleTravelExpenseApplicationDialogAction
  | SetUserAction
  | SetUserAddressIdAction
  | SetMovementLogsAction
  | SetIsMovementLogsLoadedAction
  | SetMovementRoutesAction
  | ToggleTransportationExpenseDialogAction
  | SetIsTravelExpenseApplicationDialogCompletedAction
  | SetIsTravelExpenseApplicationDialogInProgressAction
  | SetMonthSummaryAction;

const setMovementRoutes = (
  state: TravelExpensesState,
  action: SetMovementRoutesAction
) => {
  const { results } = action.payload;
  // eslint-disable-next-line no-restricted-syntax
  for (const result of results) {
    if (result.isCommutingExpense) {
      // eslint-disable-next-line no-restricted-syntax
      for (const daySummary of state.daySummaries) {
        // eslint-disable-next-line no-restricted-syntax
        for (const commutingExpense of daySummary.commutingExpenses) {
          if (commutingExpense.id === result.id) {
            commutingExpense.movementRoutes = result.movementRoutes;
          }
        }
      }
    }

    if (result.isTransportationExpense) {
      // eslint-disable-next-line no-restricted-syntax
      for (const daySummary of state.daySummaries) {
        filterZeroFareBySystem(result, daySummary.transportationExpenses);
      }
    }
  }
};

const reducer: React.Reducer<TravelExpensesState, TravelExpensesActions> = (
  currentState,
  action
) => {
  const newState: TravelExpensesState = { ...currentState };

  switch (action.type) {
    case "TOGGLE_TRANSITS":
      // 多分以下の形なんじゃないかなと思うけどよくわからんのでdisable
      // if (!newState.expandedTravelExpenses.includes(action.payload.id)) {
      // eslint-disable-next-line no-bitwise
      if (!~newState.expandedTravelExpenses.indexOf(action.payload.id)) {
        newState.expandedTravelExpenses.push(action.payload.id);
      } else {
        newState.expandedTravelExpenses.splice(
          newState.expandedTravelExpenses.indexOf(action.payload.id),
          1
        );
      }
      break;
    case "TOGGLE_COMMUTING_ROUTE_DIALOG":
      if (action.payload.commutingExpense) {
        newState.commutingRouteProp.movementLogId =
          action.payload.commutingExpense.id;
        newState.commutingRouteProp.at = action.payload.commutingExpense.at;
        newState.commutingRouteProp.arrivalId = action.payload.commutingExpense
          .arrivalName
          ? action.payload.commutingExpense.arrivalId
          : action.payload.commutingExpense.departureId;
        newState.commutingRouteProp.arrivalName = action.payload
          .commutingExpense.arrivalName
          ? action.payload.commutingExpense.arrivalName
          : action.payload.commutingExpense.departureName;
        newState.hasOutwardPath =
          action.payload.hasOutwardPath === undefined
            ? newState.hasOutwardPath
            : action.payload.hasOutwardPath;
        newState.hasReturnPath =
          action.payload.hasReturnPath === undefined
            ? newState.hasReturnPath
            : action.payload.hasReturnPath;
        newState.isReturnPath =
          action.payload.isReturnPath === undefined
            ? newState.isReturnPath
            : action.payload.isReturnPath;
      }
      newState.isCommutingRouteDialogVisible =
        !newState.isCommutingRouteDialogVisible;
      break;
    case "TOGGLE_IS_REGISTER_COMMUTING_ROUTE_IN_PROGRESS":
      newState.isRegisterCommutingRouteInProgress =
        action.payload.isRegisterCommutingRouteInProgress;
      break;
    case "TOGGLE_WORKPLACE_DIALOG":
      newState.isWorkplaceDialogVisible = !newState.isWorkplaceDialogVisible;
      break;
    case "CHANGE_WORKPLACE":
      if (action.payload.workplace) {
        newState.commutingRouteProp.arrivalId = action.payload.workplace.id;
        newState.commutingRouteProp.arrivalName = action.payload.workplace.name;
      }
      newState.isCommutingRouteDialogVisible =
        !newState.isCommutingRouteDialogVisible;
      break;
    case "TOGGLE_TRAVEL_EXPENSE_APPLICATION_DIALOG":
      newState.isTravelExpenseApplicationDialogVisible =
        !newState.isTravelExpenseApplicationDialogVisible;
      newState.isUpdateTravelExpenseApplicationDialogCompleted = false;
      newState.isUpdateTravelExpenseApplicationDialogInProgress = false;
      break;
    case "SET_USER":
      newState.user.employeeId = action.payload.employeeId;
      newState.user.name = action.payload.name;
      break;
    case "SET_USER_ADDRESS":
      if (action.payload.address) {
        newState.commutingRouteProp.departureId = action.payload.address.id;
      }
      newState.isHomeAddressNotLoaded = action.payload.address === null;
      break;
    case "SET_MOVEMENT_LOGS":
      newState.monthSummary = action.payload.monthSummary;
      newState.daySummaries = action.payload.daySummaries;
      break;
    case "SET_IS_MOVEMENT_LOGS_LOADED":
      newState.isMovementLogsLoaded = action.payload.isMovementLogsLoaded;
      break;
    case "SET_MOVEMENT_ROUTES":
      setMovementRoutes(newState, action);
      break;
    case "TOGGLE_TRANSPORTATION_EXPENSE_DIALOG":
      newState.isTransportationExpenseDialogVisible =
        !newState.isTransportationExpenseDialogVisible;
      newState.transportationExpenseDaySummary = action.payload.daySummary;
      break;
    case "SET_IS_UPDATE_TRAVEL_EXPENSE_APPLICATION_DIALOG_COMPLETED":
      newState.isUpdateTravelExpenseApplicationDialogCompleted =
        action.payload.isUpdateTravelExpenseApplicationDialogCompleted;
      break;
    case "SET_IS_UPDATE_TRAVEL_EXPENSE_APPLICATION_DIALOG_IN_PROGRESS":
      newState.isUpdateTravelExpenseApplicationDialogInProgress =
        action.payload.isUpdateTravelExpenseApplicationDialogInProgress;
      break;
    case "SET_MONTH_SUMMARY":
      newState.monthSummary = action.payload.monthSummary;
      break;
    default:
      throw new Error();
  }
  return newState;
};

const getMonthLocalString = (monthString: string) => {
  const year = monthString.split("-")[0];
  const month = monthString.split("-")[1];
  return `${year}年${month}月`;
};

const fetchMovementRoute = async (
  daySummary: DaySummary,
  getApi: ReturnType<typeof useGetMovementLogsMovementLogIdMovementRoutesApi>
) => {
  const promises = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const commutingExpense of daySummary.commutingExpenses) {
    promises.push({
      id: commutingExpense.id,
      promise: getApi(commutingExpense.id),
      isCommutingExpense: true,
      isTransportationExpense: false,
      isOutbound: commutingExpense.departureName == null,
    });
  }

  // eslint-disable-next-line no-restricted-syntax
  for (const transportationExpense of daySummary.transportationExpenses) {
    promises.push({
      id: transportationExpense.id,
      promise: getApi(transportationExpense.id),
      isCommutingExpense: false,
      isTransportationExpense: true,
      isOutbound: true,
    });
  }

  const results = [];
  // eslint-disable-next-line no-restricted-syntax
  for (const movementRoute of promises) {
    const result = await movementRoute.promise; // eslint-disable-line no-await-in-loop

    results.push({
      id: movementRoute.id,
      movementRoutes: result.reduce(
        // eslint-disable-next-line no-shadow, @typescript-eslint/no-explicit-any
        (acc: MovementRoute[], movementRoute: any) => [
          ...acc,
          getMovementRouteByAPIResponse(movementRoute),
        ],
        []
      ),
      isCommutingExpense: movementRoute.isCommutingExpense,
      isTransportationExpense: movementRoute.isTransportationExpense,
    });
  }

  return results;
};

const MonthSummaryWrapper = styled.div`
  margin: 3rem 0 0;
  width: 100%;
  @media ${RESPONSIVE.DESKTOP} {
    width: 50%;
  }
`;

const MonthSummaryTh = styled(Table.Th)`
  width: 220px;
`;

function TravelExpensesContent(): ReactElement | null {
  const auth = useContext(AuthContext);
  const [state, dispatch] = useReducer(reducer, initialState);
  const getUserAddressApi = useGetUserAddressApi();
  const getMovementLogsApi = useGetMovementLogsApi();
  const getMovementLogsMovementLogIdMovementRoutesApi =
    useGetMovementLogsMovementLogIdMovementRoutesApi();
  const deleteMovementLogApi = useDelMovementLogApi();
  const createTransportationExpenseApi = useCreateTransportationExpenseApi();
  const updateMovementLogApprovalStatusApi =
    useUpdateMovementLogApprovalStatusApi();

  const initMovementLogs = useCallback(async () => {
    try {
      const data = await getMovementLogsApi(getMonthString());
      const monthSummary = getMonthSummaryByResult(data);
      const daySummaries = getDaySummariesByResult(data);
      dispatch({
        type: "SET_MOVEMENT_LOGS",
        payload: {
          monthSummary,
          daySummaries,
        },
      });

      // eslint-disable-next-line no-restricted-syntax
      for (const daySummary of daySummaries) {
        if (state.expandedTravelExpenses.includes(daySummary.date)) {
          // eslint-disable-next-line no-await-in-loop
          const results = await fetchMovementRoute(
            daySummary,
            getMovementLogsMovementLogIdMovementRoutesApi
          );

          dispatch({
            type: "SET_MOVEMENT_ROUTES",
            payload: {
              results,
            },
          });
        }
      }
    } finally {
      dispatch({
        type: "SET_IS_MOVEMENT_LOGS_LOADED",
        payload: {
          isMovementLogsLoaded: true,
        },
      });
    }
  }, [
    getMovementLogsApi,
    getMovementLogsMovementLogIdMovementRoutesApi,
    state.expandedTravelExpenses,
  ]);

  useEffect(() => {
    (async () => {
      if (auth.user) {
        const payload = auth.user.getSignInUserSession()?.getIdToken().payload;
        if (
          payload &&
          payload["custom:employee_id"] &&
          payload["custom:name_jp"]
        ) {
          dispatch({
            type: "SET_USER",
            payload: {
              employeeId: payload["custom:employee_id"],
              name: payload["custom:name_jp"],
            },
          });
        }
      }

      initMovementLogs();
    })();
  }, [auth, initMovementLogs]);

  useEffect(() => {
    (async () => {
      const data = await getUserAddressApi();
      dispatch({
        type: "SET_USER_ADDRESS",
        payload: {
          address: data,
        },
      });
    })();
  }, [auth, getUserAddressApi]);

  const updateApprovalStatus = async (
    status: keyof typeof APPLICATION_STATUS,
    comment: string
  ) => {
    dispatch({
      type: "SET_IS_UPDATE_TRAVEL_EXPENSE_APPLICATION_DIALOG_IN_PROGRESS",
      payload: { isUpdateTravelExpenseApplicationDialogInProgress: true },
    });
    try {
      const formattedComment = getFormattedComment(
        state.monthSummary.applicantComment,
        comment
      );
      const approval = await updateMovementLogApprovalStatusApi(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        state.monthSummary.movementLogApprovalId!,
        status,
        formattedComment
      );

      const monthSummary = { ...state.monthSummary };
      monthSummary.applicationStatus = approval.approval_status;
      monthSummary.applicationStatusName = approval.approval_status_name;
      monthSummary.applicantComment = approval.applicant_comment;
      dispatch({
        type: "SET_MONTH_SUMMARY",
        payload: { monthSummary },
      });

      if (status === "unapplied") {
        dispatch({
          type: "TOGGLE_TRAVEL_EXPENSE_APPLICATION_DIALOG",
        });
      }

      dispatch({
        type: "SET_IS_UPDATE_TRAVEL_EXPENSE_APPLICATION_DIALOG_COMPLETED",
        payload: { isUpdateTravelExpenseApplicationDialogCompleted: true },
      });
    } finally {
      dispatch({
        type: "SET_IS_UPDATE_TRAVEL_EXPENSE_APPLICATION_DIALOG_IN_PROGRESS",
        payload: { isUpdateTravelExpenseApplicationDialogInProgress: false },
      });
    }
  };

  const displayMonth = getMonthString();

  return (
    <>
      <header>
        <Navbar />
      </header>
      <main role="main" className="container">
        <Section>
          <SystemNotification />
          {state.isHomeAddressNotLoaded && (
            <div className="notification is-danger is-light">
              自宅住所が登録されていないため通勤費・交通費の登録が出来ません。
            </div>
          )}
          <Layout.FlexWrapper alignItems="flex-start">
            <div>
              <Heading size={2}>通勤費・交通費</Heading>
              <Heading size={6} subtitle>
                申請月度：
                {getMonthLocalString(getMonthString())}度
              </Heading>
            </div>
            <div className="cw-travel-expense-header-actions">
              <MoveMonthButtonGroup />
            </div>
          </Layout.FlexWrapper>
          {state.isMovementLogsLoaded && state.daySummaries.length > 0 && (
            <MonthSummaryWrapper>
              <Table.Table>
                <tbody>
                  <tr>
                    <MonthSummaryTh>申請ステータス</MonthSummaryTh>
                    <Table.Td>
                      {state.monthSummary.applicationStatusName}
                    </Table.Td>
                  </tr>
                </tbody>
              </Table.Table>
              <div>
                <Heading size={4}>申請情報</Heading>
              </div>
              <Table.Table>
                <tbody>
                  <tr>
                    <MonthSummaryTh>申請者名</MonthSummaryTh>
                    <Table.Td>
                      {state.user.name} ({state.user.employeeId})
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>出勤日数</MonthSummaryTh>
                    <Table.Td>{state.monthSummary.workCount} 日</Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>あなたのコメント</MonthSummaryTh>
                    <Table.Td>
                      {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        showCommentInHTML(state.monthSummary.applicantComment!)
                      }
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>承認者のコメント</MonthSummaryTh>
                    <Table.Td>
                      {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        showCommentInHTML(state.monthSummary.approverComment!)
                      }
                    </Table.Td>
                  </tr>
                </tbody>
              </Table.Table>
              <div>
                <Heading size={4}>通勤費</Heading>
              </div>
              <Table.Table>
                <tbody>
                  <tr>
                    <MonthSummaryTh>自転車利用回数</MonthSummaryTh>
                    <Table.Td>
                      <CountLabel
                        amount={
                          state.monthSummary.commutingExpenses.bicycleCount
                        }
                      />
                      <CountDetailList
                        movementLogs={
                          state.monthSummary.commutingExpenses.bicycleList
                        }
                      />
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>マイカー利用回数</MonthSummaryTh>
                    <Table.Td>
                      <CountLabel
                        amount={state.monthSummary.commutingExpenses.myCarCount}
                      />
                      <CountDetailList
                        movementLogs={
                          state.monthSummary.commutingExpenses.myCarList
                        }
                      />
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>公共交通機関利用回数</MonthSummaryTh>
                    <Table.Td>
                      <CountLabel
                        amount={
                          state.monthSummary.commutingExpenses
                            .publicTransportationCount
                        }
                      />
                      <CountDetailList
                        movementLogs={
                          state.monthSummary.commutingExpenses
                            .publicTransportationList
                        }
                      />
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>公共交通機関利用交通費</MonthSummaryTh>
                    <Table.Td>
                      <PriceLabel
                        amount={state.monthSummary.commutingExpenses.totalFare}
                      />
                    </Table.Td>
                  </tr>
                </tbody>
              </Table.Table>
              <div>
                <i>※ 通勤費は給与振込時に、各種手当を加えた上で支給されます</i>
              </div>
              <div>
                <Heading size={4}>交通費</Heading>
              </div>
              <Table.Table>
                <tbody>
                  <tr>
                    <MonthSummaryTh>公共交通機関利用回数</MonthSummaryTh>
                    <Table.Td>
                      <CountLabel
                        amount={
                          state.monthSummary.travelExpenses
                            .publicTransportationCount
                        }
                      />
                      <CountDetailList
                        movementLogs={
                          state.monthSummary.travelExpenses
                            .publicTransportationList
                        }
                      />
                    </Table.Td>
                  </tr>
                  <tr>
                    <MonthSummaryTh>公共交通機関利用交通費</MonthSummaryTh>
                    <Table.Td>
                      <PriceLabel
                        amount={state.monthSummary.travelExpenses.totalFare}
                      />
                    </Table.Td>
                  </tr>
                </tbody>
              </Table.Table>
              <div>
                <i>※ 交通費は経費精算時に、他の経費と合わせて振込されます</i>
              </div>
              <CommuterPassesList
                commuterPasses={state.monthSummary.commuterPasses}
              />

              <Layout.FlexWrapper justifyContent="flex-end">
                {/* 申請できる条件（ステータスが未申請、過去月（当月は申請させない）、申請すべき通勤費・交通費が存在すること（出勤日数1日以上) */}
                {state.monthSummary.applicationStatus === "unapplied" &&
                  state.monthSummary.workCount > 0 &&
                  !isOverCutOffDate(dayjs(displayMonth)) && (
                    <Button
                      color="primary"
                      onClick={() => {
                        dispatch({
                          type: "TOGGLE_TRAVEL_EXPENSE_APPLICATION_DIALOG",
                        });
                      }}
                    >
                      申請
                    </Button>
                  )}

                {state.monthSummary.applicationStatus === "applying" &&
                  !isOverCutOffDate(dayjs(displayMonth)) && (
                    <Button
                      onClick={() => {
                        dispatch({
                          type: "TOGGLE_TRAVEL_EXPENSE_APPLICATION_DIALOG",
                        });
                      }}
                    >
                      取下げ
                    </Button>
                  )}
              </Layout.FlexWrapper>
            </MonthSummaryWrapper>
          )}
          <TravelExpensesTable
            daySummaries={state.daySummaries}
            isMovementLogsLoaded={state.isMovementLogsLoaded}
            expandedDaySummaries={state.expandedTravelExpenses}
            isCommutingRouteDialogVisible={state.isCommutingRouteDialogVisible}
            isWorkplaceDialogVisible={state.isWorkplaceDialogVisible}
            isEditableApplicationStatus={
              state.monthSummary.applicationStatus === "unapplied" ||
              state.monthSummary.applicationStatus === null
            }
            isHomeAddressNotLoaded={state.isHomeAddressNotLoaded}
            toggleTransits={useCallback(
              async (daySummary: DaySummary) => {
                const results = await fetchMovementRoute(
                  daySummary,
                  getMovementLogsMovementLogIdMovementRoutesApi
                );

                dispatch({
                  type: "SET_MOVEMENT_ROUTES",
                  payload: {
                    results,
                  },
                });

                dispatch({
                  type: "TOGGLE_TRANSITS",
                  payload: {
                    id: daySummary.date,
                  },
                });
              },
              [getMovementLogsMovementLogIdMovementRoutesApi]
            )}
            toggleCommutingRouteDialog={(
              commutingExpense?: CommutingExpense,
              hasOutwardPath?: boolean,
              hasReturnPath?: boolean,
              isReturnPath?: boolean
            ) => {
              dispatch({
                type: "TOGGLE_COMMUTING_ROUTE_DIALOG",
                payload: {
                  commutingExpense,
                  hasOutwardPath,
                  hasReturnPath,
                  isReturnPath,
                },
              });
            }}
            registerRoute={useCallback(
              async (
                route: PreparedRoute,
                at: string,
                isReturnPath: boolean
              ) => {
                if (!state.isRegisterCommutingRouteInProgress) {
                  dispatch({
                    type: "TOGGLE_IS_REGISTER_COMMUTING_ROUTE_IN_PROGRESS",
                    payload: {
                      isRegisterCommutingRouteInProgress: true,
                    },
                  });
                  try {
                    if (state.commutingRouteProp.movementLogId) {
                      await updateMovementLog(
                        state.commutingRouteProp.movementLogId,
                        route.id,
                        isReturnPath,
                        auth
                      );
                    } else {
                      await createMovementLog(
                        route.id,
                        at,
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        isReturnPath!,
                        auth
                      );
                    }

                    // いっそ画面の必要なデータリロードしてしまおうか。
                    const data = await getMovementLogsApi(getMonthString());
                    const monthSummary = getMonthSummaryByResult(data);
                    const daySummaries = getDaySummariesByResult(data);
                    dispatch({
                      type: "SET_MOVEMENT_LOGS",
                      payload: {
                        monthSummary,
                        daySummaries,
                      },
                    });
                    // eslint-disable-next-line no-restricted-syntax
                    for (const daySummary of daySummaries) {
                      if (
                        state.expandedTravelExpenses.includes(daySummary.date)
                      ) {
                        // eslint-disable-next-line no-await-in-loop
                        const results = await fetchMovementRoute(
                          daySummary,
                          getMovementLogsMovementLogIdMovementRoutesApi
                        );
                        dispatch({
                          type: "SET_MOVEMENT_ROUTES",
                          payload: {
                            results,
                          },
                        });
                      }
                    }
                    dispatch({
                      type: "TOGGLE_COMMUTING_ROUTE_DIALOG",
                      payload: {
                        commutingExpense: {} as CommutingExpense,
                      },
                    });
                  } finally {
                    dispatch({
                      type: "TOGGLE_IS_REGISTER_COMMUTING_ROUTE_IN_PROGRESS",
                      payload: {
                        isRegisterCommutingRouteInProgress: false,
                      },
                    });
                  }
                }
              },
              [
                state,
                auth,
                getMovementLogsMovementLogIdMovementRoutesApi,
                getMovementLogsApi,
              ]
            )}
            commutingRouteProp={state.commutingRouteProp}
            hasOutwardPath={state.hasOutwardPath}
            hasReturnPath={state.hasReturnPath}
            isReturnPath={state.isReturnPath}
            isOverCutOffDate={isOverCutOffDate(dayjs(displayMonth))}
            toggleWorkplaceDialog={() => {
              dispatch({
                type: "TOGGLE_WORKPLACE_DIALOG",
              });
            }}
            changeWorkplace={(commutingRouteProp, workplace) => {
              dispatch({
                type: "CHANGE_WORKPLACE",
                payload: {
                  commutingRouteProp,
                  workplace,
                },
              });
            }}
            handleMovementLogDelete={async (id: number) => {
              // eslint-disable-next-line no-alert
              if (window.confirm("削除します。よろしいですか？")) {
                await deleteMovementLogApi(id);

                initMovementLogs();
              }
            }}
            isTransportationExpenseDialogVisible={
              state.isTransportationExpenseDialogVisible
            }
            toggleTransportationExpenseDialog={(daySummary: DaySummary) => {
              dispatch({
                type: "TOGGLE_TRANSPORTATION_EXPENSE_DIALOG",
                payload: {
                  daySummary,
                },
              });
            }}
            transportationExpenseDaySummary={
              state.transportationExpenseDaySummary
            }
            registerTransportationExpense={async (
              movementLog: NewMovementLog
            ) => {
              await createTransportationExpenseApi(movementLog);

              initMovementLogs();
            }}
          />
        </Section>

        <TravelExpenseApplicationDialog
          toggleDialog={() => {
            dispatch({
              type: "TOGGLE_TRAVEL_EXPENSE_APPLICATION_DIALOG",
            });
          }}
          isVisible={state.isTravelExpenseApplicationDialogVisible}
          isUpdateCompleted={
            state.isUpdateTravelExpenseApplicationDialogCompleted
          }
          isUpdateInProgress={
            state.isUpdateTravelExpenseApplicationDialogInProgress
          }
          monthSummary={state.monthSummary}
          daySummaries={state.daySummaries}
          updateApprovalStatus={updateApprovalStatus}
          monthString={getMonthLocalString(getMonthString())}
        />
      </main>
    </>
  );
}

export function TravelExpenses(): ReactElement | null {
  return (
    <GlobalErrorScreen>
      <TravelExpensesContent />
    </GlobalErrorScreen>
  );
}
